import { useFormik } from "formik";
import { get, isEmpty } from "lodash";
import { useTranslation } from "react-i18next";
import PhoneWithPrefix from "shared/components/Input/PhoneWithPrefix";
import { NAME_SPACES } from "shared/locales/constants";
import {
  ACORIS_PHONE_PREFIXES,
  CONTACT_POINT_SYSTEM,
  GENDER,
} from "utils/constants";
import {
  bindInputProps,
  bindWebTextFieldInputProps,
} from "utils/helpers/input";
import ActionButtons from "../Components/ActionButtons";
import Header from "../Components/Header";
import Input from "../Components/Input";
import Layout from "../Components/Layout";
import Rgpd from "../Components/Rgpd";
import FaceFemale from "../Components/SVG/FaceFemale";
import FaceMale from "../Components/SVG/FaceMale";
import validation from "./validation";

function View({ contact, onNext, onBack, locked, translate }) {
  const { t } = useTranslation();

  const { submitForm, values, setFieldValue, validateField, ...formProps } =
    useFormik({
      initialValues: {
        ...contact,
        acceptContact: get(contact, "acceptContact", false),
        acceptDigital: get(contact, "acceptDigital", false),
        user: {
          ...get(contact, "user", {}),
          gender: get(contact, "user.gender") || GENDER.MALE,
        },
      },
      onSubmit: onNext,
      validationSchema: validation(
        t(`${NAME_SPACES.ERRORS}:VALIDATIONS`, { returnObjects: true }),
      ),
    });

  const isFormValid =
    get(values, "user.firstname") &&
    get(values, "user.lastname") &&
    get(values, `telecoms.${CONTACT_POINT_SYSTEM.EMAIL}.value`) &&
    isEmpty(formProps.errors);

  return (
    <Layout>
      <Header
        title={
          translate?.HEADER?.TITLE ||
          "Comment vous contacter afin de vous envoyer votre devis ?"
        }
        highlight={translate?.HEADER?.HIGHLIGHT || "Comment vous contacter"}
        subtitle={translate?.HEADER?.SUBTITLE || "Vos coordonnées"}
      />

      <div className="acoris-form">
        <div className="acoris-form__container">
          {/* Civilité */}
          <div className="acoris-form__civilite">
            <Input
              label={translate?.FORM?.CIVILITE?.LABEL || "Civilité"}
              type="radio"
              variant="pill"
              values={[
                {
                  value: GENDER.MALE,
                  label: translate?.FORM?.CIVILITE?.MALE || "Homme",
                  icon: (active) => <FaceMale active={active} />,
                },
                {
                  value: GENDER.FEMALE,
                  label: translate?.FORM?.CIVILITE?.FEMALE || "Femme",
                  icon: (active) => <FaceFemale active={active} />,
                },
              ]}
              {...bindInputProps({
                name: "user.gender",
                values,
                ...formProps,
              })}
              onChange={(value) => setFieldValue("user.gender", value)}
            />
          </div>

          {/* Row 1: Nom | Prénom */}
          <div className="acoris-form__row">
            <Input
              placeholder={translate?.FORM?.LASTNAME?.PLACEHOLDER || "Dubois"}
              label={translate?.FORM?.LASTNAME?.LABEL || "Nom"}
              {...bindWebTextFieldInputProps({
                name: "user.lastname",
                values,
                ...formProps,
              })}
              disabled={locked}
            />
            <Input
              placeholder={translate?.FORM?.FIRSTNAME?.PLACEHOLDER || "John"}
              label={translate?.FORM?.FIRSTNAME?.LABEL || "Prénom"}
              {...bindWebTextFieldInputProps({
                name: "user.firstname",
                values,
                ...formProps,
              })}
              disabled={locked}
            />
          </div>

          {/* Row 2: N° de téléphone | Adresse email */}
          <div className="acoris-form__row">
            <PhoneWithPrefix
              className="input-custom"
              placeholder={
                translate?.FORM?.PHONE?.PLACEHOLDER || "610203040"
              }
              label={translate?.FORM?.PHONE?.LABEL || "N° de téléphone"}
              prefixOptions={ACORIS_PHONE_PREFIXES}
              prefixValue={get(
                values,
                `telecoms.${CONTACT_POINT_SYSTEM.PHONE_NUMBER_PREFIX}.value`,
                ACORIS_PHONE_PREFIXES[0].value,
              )}
              value={get(
                values,
                `telecoms.${CONTACT_POINT_SYSTEM.PHONE}.value`,
                "",
              )}
              errors={get(
                formProps,
                `errors.telecoms.${CONTACT_POINT_SYSTEM.PHONE}.value`,
              )}
              touched={get(
                formProps,
                `touched.telecoms.${CONTACT_POINT_SYSTEM.PHONE}.value`,
              )}
              disabled={locked}
              onBlur={() =>
                formProps.setFieldTouched(
                  `telecoms.${CONTACT_POINT_SYSTEM.PHONE}.value`,
                  true,
                )
              }
              onChange={(localNumber) => {
                setFieldValue(
                  `telecoms.${CONTACT_POINT_SYSTEM.PHONE}.value`,
                  localNumber,
                );
              }}
              onPrefixChange={(prefix) => {
                setFieldValue(
                  `telecoms.${CONTACT_POINT_SYSTEM.PHONE_NUMBER_PREFIX}.value`,
                  prefix,
                );
                validateField(`telecoms.${CONTACT_POINT_SYSTEM.PHONE}.value`);
              }}
            />
            <Input
              placeholder={
                translate?.FORM?.EMAIL?.PLACEHOLDER || "exemple@domaine.fr"
              }
              label={translate?.FORM?.EMAIL?.LABEL || "Adresse email"}
              {...bindInputProps({
                name: `telecoms.${CONTACT_POINT_SYSTEM.EMAIL}.value`,
                values,
                ...formProps,
              })}
              disabled={locked}
              onChange={({ target: { value } }) => {
                setFieldValue(
                  `telecoms.${CONTACT_POINT_SYSTEM.EMAIL}.value`,
                  value,
                );
              }}
            />
          </div>

          {/* Marketing consent checkboxes — Figma node 773:2728 */}
          <div className="acoris-form__consents">
            <Input
              type="checkbox"
              variant="round"
              checked={get(values, "acceptDigital", false)}
              onChange={(checked) => setFieldValue("acceptDigital", checked)}
            >
              {translate?.CONSENT?.EMAIL_SMS ||
                "J'accepte de recevoir par e-mail et SMS des informations et propositions commerciales relatives aux offres d'assurance santé ainsi qu'aux offres d'assurance et autres produits ou services distribués par ACORIS Mutuelles et ses marques."}
            </Input>
            <Input
              type="checkbox"
              variant="round"
              checked={get(values, "acceptContact", false)}
              onChange={(checked) => setFieldValue("acceptContact", checked)}
            >
              {translate?.CONSENT?.PHONE ||
                "J'accepte d'être contacté(e) par téléphone par ACORIS Mutuelles afin de recevoir des informations et propositions commerciales relatives aux offres d'assurance santé ainsi qu'aux offres d'assurance et autres produits ou services distribués par ACORIS Mutuelles et ses marques."}
            </Input>
          </div>
          <ActionButtons
            onNext={submitForm}
            onBack={onBack}
            nextText={translate?.BUTTON?.CONTINUE}
            nextProps={{ disabled: !isFormValid }}
          />

          <Rgpd />
        </div>
      </div>
    </Layout>
  );
}

export default View;
